import React, { useState, useEffect } from 'react';
import { 
  Paperclip, 
  X, 
  File, 
  Image as ImageIcon, 
  Trash2, 
  Plus,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  FileSpreadsheet,
  FileArchive,
  Video
} from 'lucide-react';

const FileUploader = ({ 
  selectedFiles, 
  fileInputRef, 
  onFileSelect, 
  onRemoveFile, 
  onClearAll, 
  uploadingFiles 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName, mimeType) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    
    if (mimeType?.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(extension)) {
      return <ImageIcon className="w-5 h-5 text-blue-500" />;
    }
    if (mimeType?.includes('pdf') || extension === 'pdf') {
      return <FileText className="w-5 h-5 text-red-500" />;
    }
    if (['doc', 'docx', 'txt', 'rtf'].includes(extension) || mimeType?.includes('document')) {
      return <FileText className="w-5 h-5 text-blue-600" />;
    }
    if (['xls', 'xlsx', 'csv'].includes(extension) || mimeType?.includes('spreadsheet')) {
      return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    }
    if (['zip', 'rar', '7z'].includes(extension) || mimeType?.includes('archive')) {
      return <FileArchive className="w-5 h-5 text-yellow-600" />;
    }
    if (['mp4', 'mov', 'avi', 'mkv'].includes(extension) || mimeType?.startsWith('video/')) {
      return <Video className="w-5 h-5 text-purple-500" />;
    }
    return <File className="w-5 h-5 text-gray-500" />;
  };

  if (selectedFiles.length === 0) return null;

  // ========== DESKTOP VIEW ==========
  const DesktopView = () => (
    <div className="hidden lg:block bg-white rounded-xl shadow-lg border border-gray-200 p-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-50 rounded-lg">
            <Paperclip className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-lg">
              {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} ready to send
            </h3>
            <p className="text-sm text-gray-500">
              Maximum 5 files • 10MB each • Total: {formatFileSize(selectedFiles.reduce((acc, file) => acc + (file.size || 0), 0))}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {selectedFiles.length < 5 && !uploadingFiles && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 px-4 py-2 text-sm text-[#ff660e] font-medium border border-[#ff660e] rounded-lg hover:bg-orange-50 transition"
            >
              <Plus className="w-4 h-4" />
              Add Files
            </button>
          )}
          
          <button
            onClick={onClearAll}
            disabled={uploadingFiles}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition disabled:opacity-50"
            title="Remove all files"
          >
            <Trash2 className="w-4 h-4" />
            Clear All
          </button>
        </div>
      </div>

      {/* File Input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={onFileSelect}
        multiple
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.mp4,.mov,.avi"
        className="hidden"
        disabled={uploadingFiles || selectedFiles.length >= 5}
      />

      {/* Files Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {selectedFiles.map((file, index) => (
          <div
            key={index}
            className="border border-gray-200 rounded-xl p-3 hover:border-orange-300 transition-all hover:shadow-sm"
          >
            <div className="flex items-start gap-3">
              {/* File Preview */}
              {file.type === 'image' && file.previewUrl ? (
                <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                  <img
                    src={file.previewUrl}
                    alt={file.fileName}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  {getFileIcon(file.fileName, file.mimeType)}
                </div>
              )}
              
              {/* File Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">
                      {file.fileName}
                    </h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span>{formatFileSize(file.size)}</span>
                      <span>•</span>
                      <span className="capitalize">{file.type}</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => onRemoveFile(index)}
                    disabled={uploadingFiles || file.isUploading}
                    className="p-1 hover:bg-gray-100 rounded-lg transition ml-2 flex-shrink-0"
                    title="Remove file"
                  >
                    <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
                  </button>
                </div>
                
                {/* Upload Status */}
                <div className="mt-2">
                  {file.isUploading && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-orange-600 font-medium">Uploading...</span>
                        <span className="text-gray-600">{file.uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${file.uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}
                  
                  {file.uploadSuccess && (
                    <div className="flex items-center gap-2 text-green-600 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Upload complete
                    </div>
                  )}
                  
                  {file.uploadError && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span className="truncate">{file.uploadError}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Helper Info */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Files will be attached to your next message</span>
          <span>{selectedFiles.length} of 5 files selected</span>
        </div>
      </div>
    </div>
  );

  // ========== MOBILE VIEW ==========
  const MobileView = () => (
    <div className={`lg:hidden fixed left-0 right-0 bottom-0 z-50 transition-transform duration-300 ${
      isExpanded ? 'translate-y-0' : 'translate-y-full'
    }`}>
      {/* Drag Handle */}
      <div className="flex justify-center py-1">
        <div className="w-12 h-1 bg-gray-300 rounded-full" onClick={() => setIsExpanded(!isExpanded)} />
      </div>
      
      <div className="bg-white rounded-t-2xl shadow-xl border-t border-gray-200 max-h-[70vh] overflow-hidden">
        {/* Mobile Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Paperclip className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''}
                </h3>
                <p className="text-sm text-gray-500">
                  Tap to add more • Max 5 files
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={onClearAll}
                disabled={uploadingFiles}
                className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                title="Remove all"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <button
                onClick={() => setIsExpanded(false)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Add Files Button - Mobile */}
          {selectedFiles.length < 5 && !uploadingFiles && (
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full mt-4 py-3 bg-orange-50 text-[#ff660e] font-medium rounded-xl flex items-center justify-center gap-2 hover:bg-orange-100 transition"
            >
              <Plus className="w-5 h-5" />
              Add More Files
            </button>
          )}
        </div>

        {/* File Input */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileSelect}
          multiple
          accept="image/*,.pdf,.doc,.docx,.txt"
          className="hidden"
          disabled={uploadingFiles || selectedFiles.length >= 5}
        />

        {/* Files List - Mobile */}
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(70vh - 180px)' }}>
          <div className="space-y-3">
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                className="border border-gray-200 rounded-xl p-3 bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  {/* File Icon - Mobile */}
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0 border">
                    {file.type === 'image' && file.previewUrl ? (
                      <div className="w-full h-full rounded-lg overflow-hidden">
                        <img
                          src={file.previewUrl}
                          alt={file.fileName}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      getFileIcon(file.fileName, file.mimeType)
                    )}
                  </div>
                  
                  {/* File Info - Mobile */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 text-sm truncate">
                          {file.fileName}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span>{formatFileSize(file.size)}</span>
                          <span>•</span>
                          <span className="capitalize">{file.type}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => onRemoveFile(index)}
                        disabled={uploadingFiles || file.isUploading}
                        className="p-1 hover:bg-red-50 rounded-lg ml-2 flex-shrink-0"
                      >
                        <X className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                    
                    {/* Upload Status - Mobile */}
                    {file.isUploading && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-orange-600">Uploading</span>
                          <span className="text-gray-600">{file.uploadProgress}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-1.5">
                          <div
                            className="bg-orange-500 h-1.5 rounded-full transition-all"
                            style={{ width: `${file.uploadProgress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {file.uploadSuccess && (
                      <div className="flex items-center gap-1.5 mt-2 text-green-600 text-xs">
                        <CheckCircle className="w-3.5 h-3.5" />
                        Uploaded
                      </div>
                    )}
                    
                    {file.uploadError && (
                      <div className="flex items-center gap-1.5 mt-2 text-red-600 text-xs">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span className="truncate">Failed</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Uploading Status Bar - Mobile */}
        {uploadingFiles && (
          <div className="bg-orange-50 border-t border-orange-100 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 text-orange-600 animate-spin" />
                <span className="text-sm font-medium text-orange-700">Uploading files...</span>
              </div>
              <span className="text-sm text-orange-600">
                {selectedFiles.filter(f => f.uploadSuccess).length}/{selectedFiles.length}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // ========== MOBILE MINI VIEW (Collapsed) ==========
  const MobileMiniView = () => (
    <div className="lg:hidden fixed bottom-20 right-4 z-40">
      <button
        onClick={() => setIsExpanded(true)}
        className="bg-[#ff660e] text-white p-4 rounded-full shadow-lg hover:shadow-xl hover:bg-[#e55a0c] transition-all transform hover:scale-105"
      >
        <div className="relative">
          <Paperclip className="w-6 h-6" />
          {selectedFiles.length > 0 && (
            <span className="absolute -top-2 -right-2 bg-white text-[#ff660e] text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
              {selectedFiles.length}
            </span>
          )}
        </div>
      </button>
    </div>
  );

  return (
    <>
      <DesktopView />
      <MobileMiniView />
      <MobileView />
    </>
  );
};

export default FileUploader;